import type { Extension } from '@codemirror/state';
import { type EditorView, hoverTooltip, type Tooltip } from '@codemirror/view';

import { getHelperDefinition, type HelperDefinition } from './helper-registry';

/** Matches helper names after {{ or ( for nested helpers. */
const HELPER_NAME_RE = /(?:\{\{|\()\s*([A-Za-z_][A-Za-z0-9_]*)/g;

function getHelperAtLinePos(
  lineText: string,
  posInLine: number
): { helperName: string; tokenStart: number; tokenEnd: number } | null {
  const before = lineText.slice(0, posInLine);
  const after = lineText.slice(posInLine);
  const openIndex = Math.max(before.lastIndexOf('{{'), before.lastIndexOf('('));
  if (openIndex < 0) return null;

  const closeCurly = after.indexOf('}}');
  const closeParen = after.indexOf(')');
  const closeOffsetCandidates = [closeCurly, closeParen].filter((v) => v >= 0);
  const closeOffset = closeOffsetCandidates.length
    ? Math.min(...closeOffsetCandidates)
    : -1;
  if (closeOffset < 0) return null;

  const blockEnd = posInLine + closeOffset + 1;
  const blockContent = lineText.slice(openIndex, blockEnd);

  HELPER_NAME_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = HELPER_NAME_RE.exec(blockContent))) {
    const helperName = match[1];
    if (!helperName) continue;
    const tokenStart = openIndex + match.index + match[0].indexOf(helperName);
    const tokenEnd = tokenStart + helperName.length;
    if (posInLine >= tokenStart && posInLine <= tokenEnd) {
      return { helperName, tokenStart, tokenEnd };
    }
  }
  return null;
}

function getHelperAtDocPos(
  view: EditorView,
  pos: number
): { helperName: string; from: number; to: number } | null {
  try {
    const line = view.state.doc.lineAt(pos);
    const posInLine = pos - line.from;
    const helper = getHelperAtLinePos(line.text, posInLine);
    if (!helper) return null;
    return {
      helperName: helper.helperName,
      from: line.from + helper.tokenStart,
      to: line.from + helper.tokenEnd,
    };
  } catch {
    return null;
  }
}

function createCopyExampleHandler(example: string) {
  return async () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
      return;
    }
    try {
      await navigator.clipboard.writeText(example);
    } catch {
      // no-op on clipboard errors
    }
  };
}

export function buildHelperInfoDom(definition: HelperDefinition) {
  const wrapper = document.createElement('div');
  wrapper.className = 'cm-tooltip-helper';

  const header = document.createElement('div');
  header.className = 'cm-tooltip-helper-header';

  const title = document.createElement('div');
  title.className = 'cm-tooltip-helper-title';
  title.textContent = definition.name;

  const syntax = document.createElement('code');
  syntax.className = 'cm-tooltip-helper-syntax';
  syntax.textContent = definition.syntax;
  header.append(title, syntax);

  const body = document.createElement('div');
  body.className = 'cm-tooltip-helper-body';
  body.textContent = definition.documentation;

  const examplesLabel = document.createElement('div');
  examplesLabel.className = 'cm-tooltip-helper-examples-label';
  examplesLabel.textContent = 'Examples';

  const examplesList = document.createElement('div');
  examplesList.className = 'cm-tooltip-helper-examples';
  definition.examples.forEach((example) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cm-tooltip-helper-example';
    button.textContent = example;
    button.addEventListener('click', createCopyExampleHandler(example));
    examplesList.append(button);
  });

  wrapper.append(header, body, examplesLabel, examplesList);
  return wrapper;
}

export function tooltipExtension(): Extension {
  return hoverTooltip((view, pos): Tooltip | null => {
    const helper = getHelperAtDocPos(view, pos);
    if (!helper) return null;

    const definition = getHelperDefinition(helper.helperName);
    if (!definition?.documentation) return null;

    return {
      pos: helper.from,
      end: helper.to,
      above: true,
      create() {
        return {
          dom: buildHelperInfoDom(definition),
        };
      },
    };
  });
}
